import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useParams } from "react-router-dom";
import http from "../http";
import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Paper,
  useTheme,
  createTheme,
  CssBaseline,
  Toolbar,
  styled,
  Divider,
  List,
  Grid,
  InputLabel,
  Collapse,
  Stack,
  Alert,
  AlertTitle,
  IconButton,
  Box,
} from "@mui/material";
import MenuIcon from "@material-ui/icons/Menu";
import { Icon } from "@iconify/react";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import "./PDFViewer.css";
import { IAtivoTiTermoDto } from "../interfaces/IAtivoTiTermoDto";
import IAssinaturaDto from "../interfaces/IAssinaturaDto";
import { formatarDataHoraBrasil } from "../utils/formatarDataHoraBrasil";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const options = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
};

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: 340,
    width: `calc(100% - ${340}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "fixed", // Alterado para fixo
    top: 0,
    left: 0,
    height: "100vh", // Mantém altura total da tela
    width: 340,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.grey[900],
    display: "flex",
    flexDirection: "column", // Mantém os elementos empilhados
    overflow: "hidden",
  },
}));

const mdTheme = createTheme();

const formatCpf = (cpf?: string) => {
  if (!cpf) return "";
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export default function TermoPDFViewer() {
  const [open, setOpen] = React.useState(true);
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const { parametro } = useParams<{ parametro: string }>();
  const [loading, setLoading] = useState(false);
  const [binario, setBinario] = useState<any>(null);
  const [termo, setTermo] = useState<IAtivoTiTermoDto | null>(null);
  const [termoCarregado, setTermoCarregado] = useState(false);
  const [pdfCarregado, setPdfCarregado] = useState(false);
  const [openSignature, setOpenSignature] = useState(false);
  const [openErrorMessage, setOpenErrorMessage] = useState(false);
  const [assinatura, setAssinatura] = useState<IAssinaturaDto | null>(null);

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
  }

  useEffect(() => {
    document.body.style.overflowY = "auto";
    document.body.style.overflowX = "hidden !important";

    setLoading(true);

    handlePdf();
    handleTermo();
  }, []);

  useEffect(() => {
    if (pdfCarregado && termoCarregado) {
      setLoading(false);
    }
  }, [pdfCarregado, termoCarregado]);

  const handlePdf = () => {
    http
      .get(`/AtivoTiSign/GerarPdf?termo=${encodeURIComponent(parametro!)}`, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        setPdfCarregado(true);
        setBinario(response.data);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleTermo = () => {
    http
      .get<IAtivoTiTermoDto>(`/AtivoTiSign?termo=${encodeURIComponent(parametro!)}`)
      .then((response) => {
        setTermoCarregado(true);
        setTermo(response.data);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleSign = () => {
    setOpenErrorMessage(false);
    setLoading(true);

    const data: IAssinaturaDto = {
      externalReference: parametro!,
    };

    http
      .post(`/AtivoTiSign`, data)
      .then((response) => {
        setTermo(response.data);

        setSuccessMessage(true);
        setLoading(false);
      })
      .catch(() => {
        setOpenErrorMessage(true);
        setLoading(false);
      });
  };

  const handleCloseErrorMessage = () => {
    setOpenErrorMessage(false);
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage(false);
  };

  const handleClick = () => {
    if (openSignature) setOpenSignature(false);
    else setOpenSignature(true);
  };

  return (
    <>
      <div className="desktop-only">
        <Grid container>
          <Grid
            item
            sx={{
              height: "100vh", // Garante que a página ocupe toda a tela
              display: "flex",
              width: "100%",
            }}
          >
            <CssBaseline />

            <Drawer
              variant="permanent"
              open={open}
              sx={{
                width: "340px",
                backgroundColor: (theme) =>
                  theme.palette.mode === "light"
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900],
              }}
            >
              <Toolbar
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.grey[100]
                      : theme.palette.grey[900],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  px: [1],
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/logo_transparent_background.png`}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                  }}
                />
              </Toolbar>
              <Divider />
              <List
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.grey[100]
                      : theme.palette.grey[900],
                  flexGrow: 1,
                  height: "calc(100vh - 64px)", // Garante que preencha toda a altura menos a Toolbar
                  overflowY: "auto", // Permite rolagem quando necessário
                }}
                component="nav"
              >
                {termoCarregado && pdfCarregado && (
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      sx={{
                        borderTopColor: "#1768e1",
                        color: "#e6eaee",
                        backgroundColor: "white",
                        marginLeft: "12px",
                        marginRight: "12px",
                        paddingRight: "12px",
                        paddingLeft: "12px",
                        paddingTop: 0,
                        paddingBlock: "14px",
                        marginTop: "15px",
                        borderRadius: "6px",
                        borderBottom: "1px solid #1768e1",
                        borderLeft: "1px solid #1768e1",
                        borderTop: "1px solid #1768e1",
                        borderRight: "1px solid #1768e1",
                        borderStyle: "solid",
                        borderTopWidth: "6px",
                        borderRightWidth: "1px",
                        borderLeftWidth: "1px",
                        borderBottomWidth: "5px",
                        position: "relative",
                        height: "100%",
                        width: "320px",
                      }}
                    >
                      <Grid container sx={{ paddingBottom: "2px" }}>
                        <Grid item>
                          <InputLabel
                            sx={{
                              color: "#343a40",
                              fontSize: ".75em",
                              paddingRight: "10px",
                            }}
                          >
                            Status:
                          </InputLabel>
                        </Grid>
                        {termo?.status === "A" && (
                          <Grid item>
                            <InputLabel
                              sx={{
                                color: "#00c4a2",
                                fontSize: ".75em",
                                backgroundColor: "#e6eaee",
                                lineHeight: "1em",
                                borderRadius: "4px",
                                padding: "2px 4px",
                              }}
                            >
                              Assinado
                            </InputLabel>
                          </Grid>
                        )}
                        {termo?.status === "E" && (
                          <Grid item>
                            <InputLabel
                              sx={{
                                color: "#e9055a",
                                fontSize: ".75em",
                                backgroundColor: "#e6eaee",
                                lineHeight: "1em",
                                borderRadius: "4px",
                                padding: "2px 4px",
                              }}
                            >
                              Expirado
                            </InputLabel>
                          </Grid>
                        )}
                        {termo?.status === "P" && (
                          <Grid item>
                            <InputLabel
                              sx={{
                                color: "#1768e1",
                                fontSize: ".75em",
                                backgroundColor: "#e6eaee",
                                lineHeight: "1em",
                                borderRadius: "4px",
                                padding: "2px 4px",
                              }}
                            >
                              Pendente
                            </InputLabel>
                          </Grid>
                        )}
                      </Grid>
                      <Grid container sx={{ paddingBottom: "2px" }}>
                        <Grid item sx={{ width: "100%" }}>
                          <InputLabel
                            sx={{
                              color: "#343a40",
                              fontSize: ".75em",
                              paddingRight: "10px",
                              fontWeight: "bold",
                              whiteSpace: "normal",
                            }}
                          >
                            {termo?.nomeColaborador}
                          </InputLabel>
                        </Grid>
                      </Grid>
                      <Grid container sx={{ paddingBottom: "2px" }}>
                        <Grid
                          item
                          xl={1}
                          sx={{
                            alignSelf: "center",
                            color: "#868e96",
                          }}
                        >
                          <Icon
                            icon="mdi:id-card-outline"
                            style={{ verticalAlign: "text-top" }}
                          />
                        </Grid>
                        <Grid
                          item
                          sx={{
                            alignSelf: "center",
                          }}
                        >
                          <InputLabel
                            sx={{
                              color: "#343a40",
                              fontSize: ".75em",
                              paddingRight: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {formatCpf(termo?.cpfColaborador)}
                          </InputLabel>
                        </Grid>
                      </Grid>
                      <Grid container sx={{ paddingBottom: "2px" }}>
                        <Grid
                          item
                          xl={1}
                          sx={{
                            alignSelf: "center",
                            color: "#868e96",
                          }}
                        >
                          <Icon
                            icon="material-symbols:mail-outline"
                            style={{ verticalAlign: "text-top" }}
                          />
                        </Grid>
                        <Grid
                          item
                          sx={{
                            alignSelf: "center",
                          }}
                        >
                          <InputLabel
                            sx={{
                              color: "#343a40",
                              fontSize: ".75em",
                              paddingRight: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {termo?.emailColaborador}
                          </InputLabel>
                        </Grid>
                      </Grid>
                      {termo?.status === "P" && (
                        <Grid container>
                          <Grid item>
                            <Grid container>
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#e9055a",
                                    fontSize: "12px",
                                  }}
                                >
                                  Este termo expira em{" "}
                                  {termo?.dataExpiracaoFormatada}
                                </InputLabel>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {termo?.status === "E" && (
                        <Grid container>
                          <Grid item>
                            <Grid container>
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#e9055a",
                                    fontSize: "12px",
                                  }}
                                >
                                  Este termo expirou em{" "}
                                  {termo?.dataExpiracaoFormatada}
                                </InputLabel>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {termo?.status === "A" && (
                        <Grid container>
                          <Grid
                            item
                            xl={1.7}
                            sx={{
                              alignSelf: "center",
                              color: "#868e96",
                            }}
                          >
                            <Icon
                              icon="material-symbols-light:check"
                              style={{
                                verticalAlign: "text-top",
                                fontSize: "2em",
                                color: "#1768e1",
                              }}
                            />
                          </Grid>
                          <Grid item>
                            <Grid container>
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#14112d",
                                    fontSize: "12px",
                                  }}
                                >
                                  Assinou o documento
                                </InputLabel>
                              </Grid>
                            </Grid>
                            <Grid container>
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#959cae",
                                    fontSize: "11px",
                                  }}
                                >{`Em ${formatarDataHoraBrasil(
                                  termo.assinatura?.dataHora
                                )} pelo IP ${termo.assinatura?.ip}`}</InputLabel>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </List>
            </Drawer>
            <Grid component="main" sx={{ width: "100%" }}>
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                md={12}
                lg={12}
                sx={{
                  mt: 4,
                  mb: 4,
                  marginLeft: "0px",
                  marginTop: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Grid item xl={12} xs={12} sm={12} md={12} lg={12}>
                  <Backdrop
                    sx={{
                      color: "#fff",
                      zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                    open={loading}
                  >
                    <CircularProgress color="inherit" />
                  </Backdrop>
                  <Grid container>
                    <Grid item xl={1}></Grid>
                    <Grid item xl={10} sx={{ alignItems: "center" }}>
                      <Collapse in={successMessage}>
                        <Stack sx={{ width: "100%" }} spacing={2}>
                          <Alert
                            severity="success"
                            onClose={handleCloseSuccessMessage}
                          >
                            <AlertTitle>
                              Termo assinado com sucesso!
                            </AlertTitle>
                            A assinatura digital foi aplicada ao documento. Para
                            validação oficial, acesse o{" "}
                            <a
                              href="https://validar.iti.gov.br/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Validador ITI
                            </a>
                            .
                          </Alert>
                        </Stack>
                      </Collapse>
                      <Collapse in={openErrorMessage}>
                        <Stack sx={{ width: "100%" }} spacing={2}>
                          <Alert
                            severity="error"
                            onClose={handleCloseErrorMessage}
                          >
                            <AlertTitle>
                              Ocorreu um erro ao efetuar a assinatura!
                            </AlertTitle>
                            Por favor, tente novamente mais tarde.
                          </Alert>
                        </Stack>
                      </Collapse>
                      <Collapse in={termo?.expirada}>
                        <Stack sx={{ width: "100%" }} spacing={2}>
                          <Alert severity="warning">
                            <AlertTitle>
                              O termo expirou, favor entrar em contato com a
                              área de TI.
                            </AlertTitle>
                          </Alert>
                        </Stack>
                      </Collapse>
                      <Grid item xl={1}></Grid>
                    </Grid>
                  </Grid>
                  <Container
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "start",
                      height: "100vh",
                    }}
                  >
                    <div
                      style={{
                        width: 800,
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/logo.jpeg`}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                        }}
                      />
                      {binario &&
                        Array.from(new Array(numPages), (el, index) => (
                          <Paper
                            key={`page_${index + 1}`}
                            sx={{
                              padding: theme.spacing(2),
                              marginBottom: theme.spacing(2),
                              textAlign: "-webkit-center",
                            }}
                          >
                            <Document
                              file={binario}
                              onLoadSuccess={onDocumentLoadSuccess}
                              options={options}
                            >
                              <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                width={780}
                                renderTextLayer={false}
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              />
                            </Document>
                          </Paper>
                        ))}
                      {termo?.status === "P" &&
                        termoCarregado &&
                        pdfCarregado && (
                          <Button
                            variant="contained"
                            onClick={handleSign}
                            color="primary"
                            sx={{
                              position: "sticky",
                              bottom: theme.spacing(2),
                              // left: '50%',
                              transform: "translateX(-50%)",
                              zIndex: 1,
                              background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              "&:hover": {
                                background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                              },
                            }}
                          >
                            Assinar
                          </Button>
                        )}
                    </div>
                  </Container>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <div className="mobile-only">
        <Grid container>
          <Grid
            item
            sm={12}
            xs={12}
            sx={{
              verticalAlign: "middle",
            }}
          >
            <Toolbar
              sx={{
                pr: "24px",
                backgroundColor: "#1768e1",
                justifyContent: "flex-end", // Adiciona essa linha para alinhar à direita
                height: "64px",
                alignItems: "self-end",
              }}
            >
              <IconButton
                edge="start"
                aria-label="open drawer"
                sx={{
                  color: "#fff",
                  alignSelf: "stretch",
                }}
                onClick={handleClick}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Grid>
        </Grid>
        <Grid container>
          <Grid
            item
            sx={{
              display: "flex",
            }}
            xl={12}
            xs={12}
            sm={12}
            md={12}
            lg={12}
          >
            <Backdrop
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open={loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <CssBaseline />
            {openSignature && (
              <Grid item sx={{ backgroundColor: "#f5f5f5" }}>
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    height: "100vh",
                    overflow: "hidden",
                    position: "relative", // Adicionado position: 'relative'
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Grid item>
                    <Toolbar
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === "light"
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        px: [1],
                        borderRightColor: "#f5f5f5",
                      }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/logo_transparent_background.png`}
                        style={{
                          maxWidth: "50%",
                          height: "auto",
                        }}
                      />
                    </Toolbar>
                  </Grid>
                  <Divider />
                  <List
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === "light"
                          ? theme.palette.grey[100]
                          : theme.palette.grey[900],
                      flexGrow: 1,
                      height: "100vh",
                      overflow: "hidden",
                    }}
                    component="nav"
                  >
                    {termoCarregado && pdfCarregado && (
                      <Grid container>
                        <Grid
                          item
                          sx={{
                            borderTopColor: "#1768e1",
                            color: "#e6eaee",
                            backgroundColor: "white",
                            marginLeft: "12px",
                            marginRight: "12px",
                            paddingRight: "12px",
                            paddingLeft: "12px",
                            paddingTop: 0,
                            paddingBlock: "14px",
                            marginTop: "15px",
                            borderRadius: "6px",
                            borderBottom: "1px solid #1768e1",
                            borderLeft: "1px solid #1768e1",
                            borderTop: "1px solid #1768e1",
                            borderRight: "1px solid #1768e1",
                            borderStyle: "solid",
                            borderTopWidth: "6px",
                            borderRightWidth: "1px",
                            borderLeftWidth: "1px",
                            borderBottomWidth: "5px",
                            position: "relative",
                            width: "100%",
                          }}
                        >
                          <Grid container sx={{ paddingBottom: "5px" }}>
                            <Grid item>
                              <InputLabel
                                sx={{
                                  color: "#343a40",
                                  fontSize: "14px",
                                  paddingRight: "10px",
                                }}
                              >
                                Status:
                              </InputLabel>
                            </Grid>
                            {termo?.status === "A" && (
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#00c4a2",
                                    fontSize: "14px",
                                    backgroundColor: "#e6eaee",
                                    lineHeight: "1em",
                                    borderRadius: "4px",
                                    padding: "2px 4px",
                                  }}
                                >
                                  Assinado
                                </InputLabel>
                              </Grid>
                            )}
                            {termo?.status === "E" && (
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#e9055a",
                                    fontSize: "14px",
                                    backgroundColor: "#e6eaee",
                                    lineHeight: "1em",
                                    borderRadius: "4px",
                                    padding: "2px 4px",
                                  }}
                                >
                                  Expirado
                                </InputLabel>
                              </Grid>
                            )}
                            {termo?.status === "P" && (
                              <Grid item>
                                <InputLabel
                                  sx={{
                                    color: "#1768e1",
                                    fontSize: "14px",
                                    backgroundColor: "#e6eaee",
                                    lineHeight: "1em",
                                    borderRadius: "4px",
                                    padding: "2px 4px",
                                  }}
                                >
                                  Pendente
                                </InputLabel>
                              </Grid>
                            )}
                          </Grid>
                          <Grid container sx={{ paddingBottom: "5px" }}>
                            <Grid item>
                              <InputLabel
                                sx={{
                                  color: "#343a40",
                                  fontSize: "14px",
                                  paddingRight: "10px",
                                  fontWeight: "bold",
                                  whiteSpace: "normal",
                                }}
                              >
                                {termo?.nomeColaborador}
                              </InputLabel>
                            </Grid>
                          </Grid>
                          <Grid container sx={{ paddingBottom: "5px" }}>
                            <Grid
                              item
                              xs="auto"
                              sm="auto"
                              md="auto"
                              xl="auto"
                              sx={{
                                alignSelf: "center",
                                color: "#868e96",
                              }}
                            >
                              <Icon
                                icon="mdi:id-card-outline"
                                style={{
                                  verticalAlign: "text-top",
                                  fontSize: "19px",
                                }}
                              />
                            </Grid>
                            <Grid
                              item
                              xs="auto"
                              sm="auto"
                              md="auto"
                              xl="auto"
                              sx={{
                                alignSelf: "center",
                                paddingLeft: "5px",
                              }}
                            >
                              <InputLabel
                                sx={{
                                  color: "#343a40",
                                  fontSize: "14px",
                                  paddingRight: "10px",
                                  fontWeight: "bold",
                                }}
                              >
                                {formatCpf(termo?.cpfColaborador)}
                              </InputLabel>
                            </Grid>
                          </Grid>
                          <Grid container sx={{ paddingBottom: "5px" }}>
                            <Grid
                              item
                              xl={1}
                              sx={{
                                alignSelf: "center",
                                color: "#868e96",
                              }}
                            >
                              <Icon
                                icon="material-symbols:mail-outline"
                                style={{
                                  verticalAlign: "text-top",
                                  fontSize: "19px",
                                }}
                              />
                            </Grid>
                            <Grid
                              item
                              sx={{
                                alignSelf: "center",
                                paddingLeft: "5px",
                              }}
                            >
                              <InputLabel
                                sx={{
                                  color: "#343a40",
                                  fontSize: "14px",
                                  paddingRight: "10px",
                                  fontWeight: "bold",
                                }}
                              >
                                {termo?.emailColaborador}
                              </InputLabel>
                            </Grid>
                          </Grid>
                          {termo?.status === "P" && (
                            <Grid container>
                              <Grid item>
                                <Grid container>
                                  <Grid item>
                                    <InputLabel
                                      sx={{
                                        color: "#e9055a",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Este termo expira em{" "}
                                      {termo?.dataExpiracaoFormatada}
                                    </InputLabel>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                          {termo?.status === "E" && (
                            <Grid container>
                              <Grid item>
                                <Grid container>
                                  <Grid item>
                                    <InputLabel
                                      sx={{
                                        color: "#e9055a",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Este termo expirou em{" "}
                                      {termo?.dataExpiracaoFormatada}
                                    </InputLabel>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                          {termo?.status === "A" && (
                            <Grid container>
                              <Grid
                                item
                                xl={1.7}
                                sx={{
                                  alignSelf: "center",
                                  color: "#868e96",
                                }}
                              >
                                <Icon
                                  icon="material-symbols-light:check"
                                  style={{
                                    verticalAlign: "text-top",
                                    fontSize: "19px",
                                    color: "#1768e1",
                                  }}
                                />
                              </Grid>
                              <Grid item sx={{ paddingLeft: "10px" }}>
                                <Grid container>
                                  <Grid item>
                                    <InputLabel
                                      sx={{
                                        color: "#14112d",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Assinou o documento
                                    </InputLabel>
                                  </Grid>
                                </Grid>
                                <Grid container>
                                  <Grid item>
                                    <InputLabel
                                      sx={{
                                        color: "#959cae",
                                        fontSize: "14px",
                                      }}
                                    >{`Em ${formatarDataHoraBrasil(
                                      termo.assinatura?.dataHora
                                    )} pelo IP ${termo.assinatura?.ip}`}</InputLabel>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </List>
                </Box>
              </Grid>
            )}
            {!openSignature && (
              <Grid component="main" sx={{ width: "100%" }}>
                <Grid container>
                  <Grid item xl={1}></Grid>
                  <Grid item xl={10} sx={{ alignItems: "center" }}>
                    <Collapse in={successMessage}>
                      <Stack sx={{ width: "100%" }} spacing={2}>
                        <Alert
                          severity="success"
                          onClose={handleCloseSuccessMessage}
                        >
                          <AlertTitle>
                            Termo assinado com sucesso!
                          </AlertTitle>
                          A assinatura digital foi aplicada ao documento. Para
                          validação oficial, acesse o{" "}
                          <a
                            href="https://validar.iti.gov.br/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Validador ITI
                          </a>
                          .
                        </Alert>
                      </Stack>
                    </Collapse>
                    <Collapse in={openErrorMessage}>
                      <Stack sx={{ width: "100%" }} spacing={2}>
                        <Alert
                          severity="error"
                          onClose={handleCloseErrorMessage}
                        >
                          <AlertTitle>
                            Ocorreu um erro ao efetuar a assinatura!
                          </AlertTitle>
                          Por favor, tente novamente mais tarde.
                        </Alert>
                      </Stack>
                    </Collapse>
                    <Collapse in={termo?.expirada}>
                      <Stack sx={{ width: "100%" }} spacing={2}>
                        <Alert severity="warning">
                          <AlertTitle>
                            O termo expirou, favor entrar em contato com a área
                            de TI.
                          </AlertTitle>
                        </Alert>
                      </Stack>
                    </Collapse>
                    <Grid item xl={1}></Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid
                    item
                    sm={12}
                    sx={{ textAlign: "center", overflow: "auto" }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/logo.jpeg`}
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item sx={{ paddingLeft: "0px", width: "100%" }}>
                    <Container
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "start",
                      }}
                    >
                      {binario && (
                        <Document
                          file={binario}
                          onLoadSuccess={onDocumentLoadSuccess}
                          options={options}
                        >
                          {Array.from(new Array(numPages), (el, index) => (
                            <Paper
                              key={`page_${index + 1}`}
                              sx={{
                                padding: theme.spacing(2),
                                marginBottom: theme.spacing(2),
                                height: "100%",
                                display: "flex",
                                flexDirection: "column", // Adicionado flexDirection: 'column'
                                alignItems: "center",
                              }}
                            >
                              <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                width={300}
                                renderTextLayer={false}
                                sx={{ flex: 1 }} // Adicionado flex: 1
                              />
                            </Paper>
                          ))}
                        </Document>
                      )}
                      {termo?.status === "P" &&
                        termoCarregado &&
                        pdfCarregado && (
                          <Button
                            variant="contained"
                            onClick={handleSign}
                            color="primary"
                            sx={{
                              position: "fixed",
                              bottom: theme.spacing(2), // Define a distância do rodapé
                              left: "50%", // Posiciona o botão no meio da tela
                              transform: "translateX(-50%)", // Corrige o alinhamento para ficar centralizado
                              background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              zIndex: 1000, // Garante que o botão fique acima de outros elementos
                              "&:hover": {
                                background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                              },
                            }}
                          >
                            Assinar
                          </Button>
                        )}
                    </Container>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
}
